// File: src/components/OmniCine/Timeline/XR/XRRangeIntentBridge.tsx
//
// XRRangeIntentBridge (Phase N2)
// - Listens for XR range intents (begin/update/commit/cancel/remove)
// - Dispatches canonical timeline range actions:
//    - range/add (on begin)
//    - range/update (on update OR duplicate begin)
//    - range/select (on commit) [useful for loop workflows]
//    - range/remove (on cancel/remove)
// - Prevents duplicate begin from creating duplicate ranges
// - Normalizes kind/tag to canonical TimelineRangeKind buckets
// - Defaults envelope buckets to NPC lane (trackId: "n1") if not provided
// - NEW: XRSessionAuthority gate (client registration + allow/deny)
//
// A3-safe: does not mutate reducer logic; only emits existing actions.
// Mount once anywhere under OmniCineTimelineProvider (e.g., inside TimelineShell).

import React, { useEffect, useMemo, useRef } from "react";
import { useOmniCineTimeline } from "@/state/useOmniCineTimeline";
import { XRSessionAuthority } from "@/xr/session/XRSessionAuthority";

/* ------------------------------------------------------------------ */
/* Authority (module singleton)                                       */
/* ------------------------------------------------------------------ */

/**
 * Dev posture to preserve your existing verification harness:
 * - standalone
 * - enabled: true
 * - requireToken: false
 *
 * Tighten later for hosted multi-user:
 * - mode: "hosted"
 * - requireToken: true
 * - allowedTrackIds: ["n1"] for envelope-only lane edits
 *
 * ✅ Phase N2 — Option 2 (lane-scoped envelope bands)
 * XR ranges are ONLY permitted when bound to NPC lane (trackId: "n1").
 * This pairs with RangeOverlay Option 2 clipping via data-track-id anchors.
 */
const XR_AUTH = new XRSessionAuthority("standalone", {
  enabled: true,
  requireToken: false,

  // Range ops only (Phase N2)
  allowedOps: ["range/begin", "range/update", "range/commit", "range/cancel"],

  // ✅ Option 2: lane-scoped authority (NPC lane only)
  allowedTrackIds: ["n1"],

  // Keep silent by default. Uncomment for debugging:
  onDeny: (reason, intent) => {
    // eslint-disable-next-line no-console
    console.warn("[XR_AUTH] deny:", reason, intent);
  },
});

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

type XRRangeIntentDetail = {
  controllerId: string;

  // Required for begin/update (commit/cancel may omit and still be valid)
  startSec?: number;
  endSec?: number;

  // Optional taxonomy
  kind?: string;
  tag?: string;
  label?: string;

  // Optional targeting / provenance
  trackId?: string;
  source?: string; // "xr-user" recommended
};

type CanonKind =
  | "selection"
  | "loop"
  | "safety"
  | "attention"
  | "emotion"
  | "overload"
  | "music";

/* ------------------------------------------------------------------ */
/* Constants                                                          */
/* ------------------------------------------------------------------ */

const EVT_BEGIN = "omnicine:xr:range:begin";
const EVT_UPDATE = "omnicine:xr:range:update";
const EVT_COMMIT = "omnicine:xr:range:commit";
const EVT_CANCEL = "omnicine:xr:range:cancel";
// Back-compat / symmetry with marker bridge patterns
const EVT_REMOVE = "omnicine:xr:range:remove";

const MIN_RANGE_LEN_SEC = 0.001;

/* ------------------------------------------------------------------ */
/* Utilities                                                          */
/* ------------------------------------------------------------------ */

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function toNum(x: unknown, fallback = NaN): number {
  const n = Number(x);
  return Number.isFinite(n) ? n : fallback;
}

function makeId(): string {
  try {
    const c = globalThis.crypto as Crypto | undefined;
    if (c?.randomUUID) return c.randomUUID();
  } catch {
    // ignore
  }
  return `rng_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeSource(raw: unknown): "user" | "xr-user" | "ai-assist" {
  const s = String(raw ?? "xr-user").toLowerCase().trim();
  if (s === "ai-assist") return "ai-assist";
  if (s === "user") return "user";
  return "xr-user";
}

/**
 * Treat incoming (tag/kind) as a “tag” first, then map to canonical buckets.
 */
function normalizeTag(raw: unknown): string {
  const t = String(raw ?? "selection").toLowerCase().trim();
  if (t === "safe") return "safety";
  return t;
}

function mapTagToCanon(tag: string): CanonKind {
  if (
    tag === "selection" ||
    tag === "loop" ||
    tag === "safety" ||
    tag === "attention" ||
    tag === "emotion" ||
    tag === "overload" ||
    tag === "music"
  ) {
    return tag as CanonKind;
  }

  // audio taxonomy -> music
  if (tag === "sfx" || tag === "silence" || tag === "voice") return "music";

  // npc behavior taxonomy -> envelope buckets
  if (tag === "awareness" || tag === "curiosity" || tag === "obedience") return "attention";
  if (tag === "fear" || tag === "aggression") return "overload";

  return "emotion";
}

/**
 * Phase N2 Option 2 envelope kinds (lane-scoped).
 * Include safety so XR "safe"/"safety" can clip to lane if desired.
 */
function isEnvelopeBucket(kind: CanonKind): boolean {
  return kind === "attention" || kind === "emotion" || kind === "overload" || kind === "safety";
}

function normalizeDetail(raw: unknown): XRRangeIntentDetail | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;

  const controllerId = String(r.controllerId ?? "").trim();
  if (!controllerId) return null;

  const startSec = r.startSec != null ? toNum(r.startSec) : undefined;
  const endSec = r.endSec != null ? toNum(r.endSec) : undefined;

  const kind = r.kind != null ? String(r.kind) : undefined;
  const tag = r.tag != null ? String(r.tag) : undefined;
  const trackId = r.trackId != null ? String(r.trackId) : undefined;
  const source = r.source != null ? String(r.source) : "xr-user";
  const label = r.label != null ? String(r.label) : undefined;

  return { controllerId, startSec, endSec, kind, tag, trackId, source, label };
}

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */

export default function XRRangeIntentBridge(): null {
  const { state, dispatch } = useOmniCineTimeline();

  /**
   * One active range id per controllerId.
   * Prevents begin->begin duplicates and routes updates correctly.
   */
  const activeByController = useRef<Map<string, string>>(new Map());

  const api = useMemo(() => {
    const duration = () => state.durationSec;

    const computeRange = (d: XRRangeIntentDetail) => {
      const dur = duration();

      const s0 = toNum(d.startSec, NaN);
      const e0 = toNum(d.endSec, NaN);
      if (!Number.isFinite(s0) || !Number.isFinite(e0)) return null;

      const a = clamp(Math.min(s0, e0), 0, dur);
      const b = clamp(Math.max(s0, e0), 0, dur);

      const s = a;
      const e = clamp(b, s + MIN_RANGE_LEN_SEC, dur);

      // Tag precedence: explicit tag > kind > default
      const rawTag = normalizeTag(d.tag ?? d.kind ?? "selection");
      const canon = mapTagToCanon(rawTag);

      // Lane targeting:
      // - if XR provides trackId, use it
      // - else default envelope buckets to NPC lane n1
      const computedTrackId =
        typeof d.trackId === "string" && d.trackId.trim()
          ? d.trackId.trim()
          : isEnvelopeBucket(canon)
            ? "n1"
            : undefined;

      return { s, e, rawTag, canon, trackId: computedTrackId };
    };

    const allow = (
      op: "range/begin" | "range/update" | "range/commit" | "range/cancel",
      d: XRRangeIntentDetail,
      trackId?: string,
    ): boolean => {
      const clientId = d.controllerId;

      // Per your current XRSessionAuthority contract, this registers the controller/client.
      // Keep it stable; the authority object is module-singleton.
      XR_AUTH.registerClient(clientId);

      return XR_AUTH.validateXRIntent({
        op,
        trackId,
        meta: { source: "xr-user", clientId },
      });
    };

    const addRange = (d: XRRangeIntentDetail) => {
      const computed = computeRange(d);
      if (!computed) return;

      const { s, e, rawTag, canon, trackId } = computed;

      // Option 2 enforcement: if the computed trackId isn't n1, authority should deny.
      if (!allow("range/begin", d, trackId)) return;

      const id = makeId();
      activeByController.current.set(d.controllerId, id);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const range: any = {
        id,
        startSec: s,
        endSec: e,
        kind: canon,
        source: normalizeSource(d.source),
        ...(trackId ? { trackId: String(trackId) } : null),
      };

      // Optional metadata passthrough (runtime-safe)
      range.tag = rawTag;
      if (typeof d.label === "string" && d.label.trim()) range.label = d.label.trim();

      dispatch({
        type: "range/add",
        payload: { range },
        meta: { source: normalizeSource(d.source), transient: true, note: "xr range begin" },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
    };

    const updateRange = (d: XRRangeIntentDetail) => {
      const computed = computeRange(d);
      if (!computed) return;

      const { s, e, trackId } = computed;
      if (!allow("range/update", d, trackId)) return;

      const id = activeByController.current.get(d.controllerId);
      if (!id) {
        addRange(d); // fail-safe begin
        return;
      }

      dispatch({
        type: "range/update",
        payload: { rangeId: id, startSec: s, endSec: e },
        meta: { source: normalizeSource(d.source), transient: true, note: "xr range update" },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
    };

    const commitRange = (d: XRRangeIntentDetail) => {
      const id = activeByController.current.get(d.controllerId);
      if (!id) return;

      // Commit does not rely on recomputing range fields.
      if (!allow("range/commit", d)) return;

      activeByController.current.delete(d.controllerId);

      dispatch({
        type: "range/select",
        payload: { rangeId: id },
        meta: { source: normalizeSource(d.source), note: "xr range commit/select" },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
    };

    const cancelRange = (d: XRRangeIntentDetail) => {
      const id = activeByController.current.get(d.controllerId);
      if (!id) return;

      if (!allow("range/cancel", d)) return;

      activeByController.current.delete(d.controllerId);

      dispatch({
        type: "range/remove",
        payload: { rangeId: id },
        meta: { source: normalizeSource(d.source), note: "xr range cancel/remove" },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
    };

    // Duplicate begin => update (prevents duplicates)
    const beginOrUpdate = (d: XRRangeIntentDetail) => {
      const id = activeByController.current.get(d.controllerId);
      if (id) updateRange(d);
      else addRange(d);
    };

    return { beginOrUpdate, updateRange, commitRange, cancelRange };
  }, [dispatch, state.durationSec]);

  useEffect(() => {
    const onBegin = (ev: Event) => {
      const d = normalizeDetail((ev as CustomEvent).detail);
      if (!d) return;
      api.beginOrUpdate(d);
    };

    const onUpdate = (ev: Event) => {
      const d = normalizeDetail((ev as CustomEvent).detail);
      if (!d) return;
      api.updateRange(d);
    };

    const onCommit = (ev: Event) => {
      const d = normalizeDetail((ev as CustomEvent).detail);
      if (!d) return;
      api.commitRange(d);
    };

    const onCancel = (ev: Event) => {
      const d = normalizeDetail((ev as CustomEvent).detail);
      if (!d) return;
      api.cancelRange(d);
    };

    // Treat remove as cancel (authority op taxonomy stays small)
    const onRemove = (ev: Event) => {
      const d = normalizeDetail((ev as CustomEvent).detail);
      if (!d) return;
      api.cancelRange(d);
    };

    window.addEventListener(EVT_BEGIN, onBegin as EventListener);
    window.addEventListener(EVT_UPDATE, onUpdate as EventListener);
    window.addEventListener(EVT_COMMIT, onCommit as EventListener);
    window.addEventListener(EVT_CANCEL, onCancel as EventListener);
    window.addEventListener(EVT_REMOVE, onRemove as EventListener);

    return () => {
      window.removeEventListener(EVT_BEGIN, onBegin as EventListener);
      window.removeEventListener(EVT_UPDATE, onUpdate as EventListener);
      window.removeEventListener(EVT_COMMIT, onCommit as EventListener);
      window.removeEventListener(EVT_CANCEL, onCancel as EventListener);
      window.removeEventListener(EVT_REMOVE, onRemove as EventListener);
    };
  }, [api]);

  // StrictMode-safe cleanup to prevent orphan ranges if the bridge unmounts mid-gesture
  useEffect(() => {
    return () => {
      const active = activeByController.current;
      if (!active.size) return;

      for (const [, rangeId] of active.entries()) {
        dispatch({
          type: "range/remove",
          payload: { rangeId },
          meta: { source: "xr-user", note: "xr bridge unmount cleanup" },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any);
      }

      active.clear();
    };
  }, [dispatch]);

  return null;
}
